import React, { useState, useRef } from "react";
import { Camera, Upload, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { aiService } from "../services/aiService";

export interface ClubIdentification {
  brand: string;
  model: string;
  clubType: string;
  shaft?: {
    brand?: string;
    model?: string;
    flex?: string;
    type?: string;
  };
  loft?: number;
  confidence: "high" | "medium" | "low";
}

interface ClubPhotoCaptureProps {
  onClubIdentified: (club: ClubIdentification) => void;
  onCancel: () => void;
}

const ClubPhotoCapture: React.FC<ClubPhotoCaptureProps> = ({
  onClubIdentified,
  onCancel,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ClubIdentification | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10MB.");
      return;
    }

    setError(null);
    setResult(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setImagePreview(dataUrl);
      analyzeImage(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async (base64Image: string) => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const identification = await aiService.identifyClubFromPhoto(base64Image);
      setResult(identification);
    } catch (err: any) {
      console.error("Club identification failed:", err);
      setError(
        err.message || "Could not identify the club. Try a clearer photo."
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleConfirm = () => {
    if (result) {
      onClubIdentified(result);
    }
  };

  const handleRetake = () => {
    setImagePreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Camera className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Identify Your Club</h3>
                <p className="text-green-100 text-sm">
                  Snap a photo or upload an image
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-white/70 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {!imagePreview ? (
            <>
              {/* Camera/Upload Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-green-300 rounded-xl hover:bg-green-50 hover:border-green-400 transition-all"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <Camera className="w-7 h-7 text-green-600" />
                  </div>
                  <span className="font-semibold text-gray-700 text-sm">
                    Take Photo
                  </span>
                </button>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-dashed border-blue-300 rounded-xl hover:bg-blue-50 hover:border-blue-400 transition-all"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <Upload className="w-7 h-7 text-blue-600" />
                  </div>
                  <span className="font-semibold text-gray-700 text-sm">
                    Upload Image
                  </span>
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                For best results, photograph the club head and shaft label
                clearly
              </p>

              {/* Hidden inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          ) : (
            <>
              {/* Image Preview */}
              <div className="relative rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={imagePreview}
                  alt="Uploaded club"
                  className="w-full h-48 object-cover"
                />
                {isAnalyzing && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Loader2 className="w-10 h-10 animate-spin mx-auto mb-2" />
                      <p className="font-semibold">Identifying club...</p>
                      <p className="text-sm text-white/70">
                        AI is analyzing your photo
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-800 font-medium">{error}</p>
                    <button
                      onClick={handleRetake}
                      className="text-sm text-red-600 underline mt-1"
                    >
                      Try another photo
                    </button>
                  </div>
                </div>
              )}

              {/* Result */}
              {result && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold text-sm">
                      Club identified
                      {result.confidence === "high"
                        ? " (high confidence)"
                        : result.confidence === "medium"
                          ? " (medium confidence)"
                          : " (low confidence - please verify)"}
                    </span>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Type:</span>{" "}
                        <span className="font-semibold text-gray-900">
                          {result.clubType}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Brand:</span>{" "}
                        <span className="font-semibold text-gray-900">
                          {result.brand}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Model:</span>{" "}
                        <span className="font-semibold text-gray-900">
                          {result.model}
                        </span>
                      </div>
                      {result.loft && (
                        <div>
                          <span className="text-gray-500">Loft:</span>{" "}
                          <span className="font-semibold text-gray-900">
                            {result.loft}&deg;
                          </span>
                        </div>
                      )}
                      {result.shaft?.brand && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Shaft:</span>{" "}
                          <span className="font-semibold text-gray-900">
                            {result.shaft.brand} {result.shaft.model}{" "}
                            {result.shaft.flex}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleRetake}
                      className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                    >
                      Retake
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="flex-[2] px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    >
                      Use This Club
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubPhotoCapture;
